import { useRef, useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import cx from 'classnames'
import './Modal.scss'
import { AppButton } from '@components/AppComponents'
import { FullscreenOutlined, CompressOutlined } from '@ant-design/icons'
interface Props {
  open?: boolean
  maskClosable?: boolean
  width?: number | string
  defaultHeight?: number
  onCancel?: () => void
  title?: React.ReactNode
  footer?: React.ReactNode
  className?: string
  noFooter?: boolean
  children?: React.ReactNode
  bodyStyle?: any
  resizable?: boolean
  draggable?: boolean
  fullscreenMode?: boolean
}

type ResizeDirection =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'topleft'
  | 'topright'
  | 'bottomleft'
  | 'bottomright'
  | ''
const minY = 50
const defaultW = 800
const defaultH = 400

function AppModal(props: Props) {
  const {
    open,
    onCancel,
    title,
    footer,
    className,
    noFooter,
    children,
    width = defaultW,
    defaultHeight,
    resizable = false,
    // draggable = true,
    fullscreenMode = false,
    maskClosable = false,
  } = props

  const draggable = false // Enable dragging only if resizable is true
  const [position, setPosition] = useState({ x: null, y: null })
  const [size, setSize] = useState({
    width: typeof width === 'number' ? width : defaultW,
    height: defaultHeight,
  })
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection>('')
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 })
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 })
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 })
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [preFullscreenState, setPreFullscreenState] = useState<{
    pos: { x: number; y: number }
    size: { width: number; height: number }
  } | null>(null)

  const modalRef = useRef<HTMLDivElement>(null)

  // Initialize position to center
  useEffect(() => {
    if (open && !isFullscreen && fullscreenMode) {
      const initialW = typeof width === 'number' ? width : defaultW
      const modalHeight = defaultHeight || defaultH

      if (fullscreenMode) {
        // Use window inner dimensions to center
        const x = Math.max(0, (window.innerWidth - initialW) / 2)
        const y = Math.max(0, (window.innerHeight - modalHeight) / 2)
        setPosition({ x, y })
      }

      setSize({ width: initialW, height: modalHeight })
    }
  }, [open, width, defaultHeight])

  // Dragging logic
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isFullscreen || !draggable) return
    const target = e.target as HTMLElement
    // Only drag if clicking specifically on the header area (we'll add a specific class or check parent)
    if (target.closest('.modal-drag-handle')) {
      setIsDragging(true)
      const rect = modalRef.current?.getBoundingClientRect()
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        })
      }
    }
  }

  // Resizing logic
  const handleResizeMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    direction: ResizeDirection
  ) => {
    if (!resizable || isFullscreen) return
    e.stopPropagation()
    setIsResizing(true)
    setResizeDirection(direction)
    setInitialMousePos({ x: e.clientX, y: e.clientY })
    setInitialSize({ ...size })
    setInitialPosition({ ...position })
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && draggable && !isFullscreen) {
      const newX = Math.max(
        0,
        Math.min(window.innerWidth - size.width, e.clientX - dragOffset.x)
      )
      const maxY = Math.max(minY, window.innerHeight - size.height)
      const newY = Math.max(minY, Math.min(maxY, e.clientY - dragOffset.y))
      setPosition({ x: newX, y: newY })
    }

    if (isResizing && resizable && !isFullscreen) {
      const deltaX = e.clientX - initialMousePos.x
      const deltaY = e.clientY - initialMousePos.y

      let newWidth = initialSize.width
      let newHeight = initialSize.height
      let newX = initialPosition.x
      let newY = initialPosition.y

      if (resizeDirection.includes('right')) {
        newWidth = Math.max(300, initialSize.width + deltaX)
      }
      if (resizeDirection.includes('left')) {
        newWidth = Math.max(300, initialSize.width - deltaX)
        newX = Math.min(
          initialPosition.x + deltaX,
          initialPosition.x + initialSize.width - 300
        )
      }
      if (resizeDirection.includes('bottom')) {
        newHeight = Math.max(200, initialSize.height + deltaY)
      }
      if (resizeDirection.includes('top')) {
        newHeight = Math.max(200, initialSize.height - deltaY)
        newY = Math.min(
          initialPosition.y + deltaY,
          initialPosition.y + initialSize.height - 200
        )
      }

      // Constraints
      if (newX + newWidth > window.innerWidth)
        newWidth = window.innerWidth - newX
      if (newY < minY) {
        newHeight -= minY - newY
        newY = minY
      }
      if (newY + newHeight > window.innerHeight)
        newHeight = window.innerHeight - newY

      setSize({ width: newWidth, height: newHeight })
      setPosition({ x: newX, y: newY })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    setIsResizing(false)
    setResizeDirection('')
  }

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, isResizing, size, position])

  const toggleFullscreen = () => {
    if (isFullscreen) {
      // Restore
      if (preFullscreenState) {
        setPosition(preFullscreenState.pos)
        setSize(preFullscreenState.size)
      }
      setIsFullscreen(false)
    } else {
      // Go full
      setPreFullscreenState({ pos: position, size: size })
      setPosition({ x: 0, y: minY })
      setSize({ width: window.innerWidth, height: window.innerHeight - minY })
      setIsFullscreen(true)
    }
  }

  const renderModalFooter = () => {
    if (!footer) {
      if (noFooter) return null
      return (
        <div className="flex justify-center p-2">
          <AppButton onClick={onCancel} rounded color="red">
            Close
          </AppButton>
        </div>
      )
    }
    return (
      <div
        className="gap-2 flex justify-center p-2 mb-2"
        onClick={e => e.stopPropagation()}
      >
        {footer}
      </div>
    )
  }

  const modalStyles = useMemo(() => {
    if (isFullscreen) {
      return {
        width: '100vw',
        height: 'calc(100vh)',
        top: '0px',
        maxWidth: '100vw',
        maxHeight: 'calc(100vh)',
      }
    } else {
      return {
        left: '50%',
        top: 'calc(50%)',
        transform: 'translate(-50%, calc(-50%))',
        width: width ?? 'auto',
        height: defaultHeight ? `${defaultHeight}px` : 'auto',
        maxWidth: '100vw',
        maxHeight: 'calc(100vh - 60px)',
      }
    }
  }, [isFullscreen, size, position, fullscreenMode])

  if (!open) return null

  const modalContent = (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black opacity-40"
        onClick={() => {
          if (maskClosable && onCancel) onCancel()
        }}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={cx(
          'bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col absolute',
          className,
          { fullscreen: isFullscreen }
        )}
        style={modalStyles}
        onMouseDown={handleMouseDown}
      >
        {/* Header */}
        <div
          className={cx(
            'flex justify-between items-center px-4 py-2 bg-gray-50',
            {
              'modal-drag-handle cursor-move select-none':
                draggable && !isFullscreen,
              'border-b border-grey-200': fullscreenMode,
            }
          )}
        >
          <div className="font-semibold text-lg truncate flex-1">{title}</div>
          <div className="flex items-center gap-2">
            {fullscreenMode && (
              <div
                className="cursor-pointer hover:bg-gray-500 rounded transition-colors"
                onClick={toggleFullscreen}
              >
                {isFullscreen ? (
                  <CompressOutlined
                    title="Exit Full Screen"
                    className="text-lg"
                  />
                ) : (
                  <FullscreenOutlined title="Full Screen" className="text-lg" />
                )}
              </div>
            )}
            {/* <div
              className="cursor-pointer hover:bg-red-100 hover:text-red-500 p-1 rounded transition-colors"
              onClick={onCancel}
            >
              <CloseOutlined />
            </div> */}
          </div>
        </div>

        {/* Body */}
        <div
          className="flex-1 overflow-auto p-4"
          onClick={e => e.stopPropagation()}
        >
          {children}
        </div>

        {/* Footer */}
        {renderModalFooter()}

        {/* Resizers */}
        {resizable && !isFullscreen && (
          <>
            <div
              className="absolute top-0 left-0 w-full h-1 cursor-ns-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'top')}
            />
            <div
              className="absolute bottom-0 left-0 w-full h-1 cursor-ns-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'bottom')}
            />
            <div
              className="absolute top-0 left-0 h-full w-1 cursor-ew-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'left')}
            />
            <div
              className="absolute top-0 right-0 h-full w-1 cursor-ew-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'right')}
            />
            <div
              className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize z-10"
              onMouseDown={e => handleResizeMouseDown(e, 'topleft')}
            />
            <div
              className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize z-10"
              onMouseDown={e => handleResizeMouseDown(e, 'topright')}
            />
            <div
              className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize z-10"
              onMouseDown={e => handleResizeMouseDown(e, 'bottomleft')}
            />
            <div
              className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize z-10"
              onMouseDown={e => handleResizeMouseDown(e, 'bottomright')}
            />
          </>
        )}
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default AppModal
